import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Button, Group, Select, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCards, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type {
  DrawSpreadCardInput,
  MoveSpreadCardInput,
  SpreadPlacedCardView,
  SpreadSessionView,
  DeckSummary,
} from '@creativitas/shared';
import { PlayingCard } from '@/components/domain/PlayingCard';
import { fetchDecks } from '@/features/content/api';
import { TABLE_TEXTURE } from '@/lib/assets';
import { CardPreviewModal } from './CardPreviewModal';
import classes from './SpreadCanvas.module.css';

type FreeformSpreadProps = {
  session: SpreadSessionView;
  /*
    Другие действия со столом ещё выполняются. Сохранение перемещения сюда не
    входит: карта уже стоит на новом месте, и если на время запроса гасить
    колоду и кнопки, они мигают после каждого перетаскивания.
  */
  busy: boolean;
  onDraw: (input: DrawSpreadCardInput) => void;
  onMove: (cardId: string, input: MoveSpreadCardInput) => Promise<SpreadSessionView>;
  onRemove: (cardId: string) => void;
  /** Owner-only: in a group session the table is shared by everyone. */
  onClear?: () => void;
  /** Почему сейчас тянуть нельзя: очередь другого участника (§бэклог 9). */
  lockedReason?: string | null;
  fetchAvailableDecks?: () => Promise<{ items: DeckSummary[] }>;
};

const clamp = (value: number) => Math.max(6, Math.min(94, Math.round(value)));

export function FreeformSpread({
  session,
  busy,
  onDraw,
  onMove,
  onRemove,
  onClear,
  fetchAvailableDecks,
  lockedReason,
}: FreeformSpreadProps) {
  const [deckId, setDeckId] = useState<string | null>(null);
  const [preview, setPreview] = useState<SpreadPlacedCardView | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [pendingPositions, setPendingPositions] = useState<Record<string, {
    sequence: number;
    position: MoveSpreadCardInput;
    savedVersion: number | null;
  }>>({});
  const sequence = useRef(0);
  const saveQueue = useRef(Promise.resolve());
  const cards = session.placedCards.map((card) => {
    const pending = pendingPositions[card.id];
    return pending && (pending.savedVersion === null || session.stateVersion < pending.savedVersion)
      ? { ...card, ...pending.position }
      : card;
  });
  useEffect(() => {
    setPendingPositions((current) => {
      const finished = Object.keys(current).filter((id) => {
        const version = current[id]!.savedVersion;
        return version !== null && session.stateVersion >= version;
      });
      if (finished.length === 0) return current;
      const remaining = { ...current };
      for (const id of finished) delete remaining[id];
      return remaining;
    });
  }, [pendingPositions, session.stateVersion]);
  const decksQuery = useQuery({
    queryKey: ['decks', 'freeform', fetchAvailableDecks ? session.id : 'member'],
    queryFn: fetchAvailableDecks ?? (() => fetchDecks({ freeformOnly: true })),
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));
  const active = session.status === 'active';
  /*
    Свободный стол на своей доске — «Своя игра» (инструкция «Шахматы»).

    Доска квадратная, и карта на ней считается от клетки, а не в пикселях:
    132 px на доске в 640 px закрыли бы полторы клетки, и «разложите карты по
    клеткам» перестало бы быть выполнимым.
  */
  const board = session.spread.boardImage;
  const grid = session.spread.boardGrid ?? 8;
  const maxZ = Math.max(-1, ...cards.map((card) => card.zIndex));

  /* Sweeping the table cannot be undone, so it asks — same as removing a player. */
  const askClear = () => {
    if (!onClear) return;
    modals.openConfirmModal({
      title: 'Очистить стол?',
      children: (
        <Text size="sm" c="var(--text-muted)">
          Со стола уйдут все карты — {session.placedCards.length}. Вернуть их обратно нельзя,
          придётся вытягивать заново.
        </Text>
      ),
      labels: { confirm: 'Очистить', cancel: 'Оставить' },
      confirmProps: { color: 'red' },
      onConfirm: onClear,
    });
  };

  const options = useMemo(
    () => (decksQuery.data?.items ?? [])
      .filter((deck) => deck.access.allowed)
      .map((deck) => ({ value: deck.id, label: `${deck.title} · ${deck.cardCount} карт` })),
    [decksQuery.data],
  );

  const handleDraw = () => {
    if (!deckId) return;
    const offset = session.placedCards.length;
    onDraw({
      deckId,
      x: 32 + ((offset * 11) % 38),
      y: 34 + ((offset * 9) % 34),
    });
  };

  const handleDragEnd = ({ active: dragged, delta }: DragEndEvent) => {
    const card = cards.find((item) => item.id === dragged.id);
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!card || !bounds || bounds.width === 0 || bounds.height === 0 || (delta.x === 0 && delta.y === 0)) return;
    const position: MoveSpreadCardInput = {
      x: clamp(card.x + (delta.x / bounds.width) * 100),
      y: clamp(card.y + (delta.y / bounds.height) * 100),
      zIndex: maxZ + 1,
    };
    const moveSequence = ++sequence.current;
    // Keep the drop visible while HTTP or socket snapshots still contain older coordinates.
    setPendingPositions((current) => ({ ...current, [card.id]: { sequence: moveSequence, position, savedVersion: null } }));
    // Serialize saves so a repeated drag cannot finish on the server in the wrong order.
    saveQueue.current = saveQueue.current
      .then(async () => {
        const saved = await onMove(card.id, position);
        // Query subscribers may receive the HTTP result one render later.
        setPendingPositions((current) => current[card.id]?.sequence === moveSequence
          ? { ...current, [card.id]: { ...current[card.id]!, savedVersion: saved.stateVersion } }
          : current);
      })
      .catch(() => {
        // The calling page shows the error; only this card returns to server coordinates.
        setPendingPositions((current) => {
          if (current[card.id]?.sequence !== moveSequence) return current;
          const remaining = { ...current };
          delete remaining[card.id];
          return remaining;
        });
      });
  };

  const handleRemove = (cardId: string) => {
    onRemove(cardId);
    setPreview(null);
  };

  return (
    <Stack gap="md">
      {lockedReason ? (
        <Text size="sm" c="var(--text-muted)" data-testid="spread-turn-lock">
          {lockedReason}
        </Text>
      ) : null}
      {active ? (
        <Group align="flex-end" wrap="wrap">
          <Select
            label="Колода"
            placeholder={decksQuery.isLoading ? 'Загружаем...' : 'Выберите колоду'}
            data={options}
            value={deckId}
            onChange={setDeckId}
            searchable
            disabled={decksQuery.isLoading || busy}
            style={{ flex: '1 1 300px' }}
          />
          <Button
            leftSection={<IconCards size={17} />}
            disabled={!deckId || busy || Boolean(lockedReason)}
            loading={busy}
            onClick={handleDraw}
          >
            Вытянуть карту
          </Button>
          {onClear ? (
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={17} />}
              disabled={busy || session.placedCards.length === 0}
              onClick={askClear}
            >
              Очистить стол
            </Button>
          ) : null}
        </Group>
      ) : null}

      {active && !decksQuery.isLoading && options.length === 0 ? (
        <Alert color="gold" icon={<IconInfoCircle size={18} />}>
          Нет доступных колод для свободного стола. Администратор может включить колоду и настроить доступ.
        </Alert>
      ) : null}

      <div className={board ? classes.boardViewport : classes.viewport}>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div
            ref={canvasRef}
            className={board ? classes.boardCanvas : classes.canvas}
            data-testid="freeform-canvas"
            style={
              (board
                ? {
                    '--spread-board-image': `url(${board})`,
                    '--spread-board-grid': grid,
                    '--spread-card-width': `calc(100% / ${grid} * 0.92)`,
                  }
                : { '--spread-table-image': `url(${TABLE_TEXTURE})` }) as unknown as CSSProperties
            }
          >
            {session.placedCards.length === 0 ? (
              <Text component="div" className={classes.emptyCanvasNote}>
                Выберите колоду и вытяните первую карту. Карты перетаскиваются мышью, по нажатию открываются крупно.
              </Text>
            ) : null}
            {cards.map((card) => (
              <DraggableCard
                key={card.id}
                card={card}
                disabled={!active || busy}
                onPreview={() => setPreview(card)}
              />
            ))}
          </div>
        </DndContext>
      </div>

      <CardPreviewModal
        card={preview}
        busy={busy}
        onClose={() => setPreview(null)}
        onRemove={active ? handleRemove : undefined}
      />
    </Stack>
  );
}

function DraggableCard({
  card,
  disabled,
  onPreview,
}: {
  card: SpreadPlacedCardView;
  disabled: boolean;
  onPreview: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: card.id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      className={classes.freeformCard}
      data-testid={`freeform-card-${card.id}`}
      style={{
        left: `${card.x}%`,
        top: `${card.y}%`,
        zIndex: isDragging ? 10_001 : card.zIndex,
        transform: `translate(-50%, -50%) ${CSS.Transform.toString(transform) ?? ''}`,
      }}
      {...listeners}
      {...attributes}
    >
      <PlayingCard
        frontImage={card.card.frontImage}
        backImage={card.card.backImage}
        faceUp={card.revealed}
        label={card.card.title ?? `${card.deckTitle}, карта ${card.card.position}`}
        width="100%"
        onClick={onPreview}
      />
    </div>
  );
}
